import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, CardBody, Badge, Table, Button, Spinner, Alert,
  CardFooter
} from 'reactstrap';
import {
  FileText, CheckCircle, Truck, User, Calendar, ExternalLink, Download, Package
} from 'lucide-react';
import { API_URL } from '../../utils/Constants/URLS';
import CreateWorkOrder from '../CreateWorkOrder';
import CreatePurchaseOrder from '../CreatePurchaseOrder';

const ViewQuotation = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [woModalOpen, setWoModalOpen] = useState(false);
  const [selectedWoDoc, setSelectedWoDoc] = useState(null);

  // Create PO modal
  const [poModalOpen, setPoModalOpen] = useState(false);
  const [selectedPoDoc, setSelectedPoDoc] = useState(null);

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchQuotations();
  }, []);

  const openPOModal = (doc) => {
    if (!doc.orderGivenTo?.vendor?._id) {
      alert('No vendor has been awarded this quotation yet.');
      return;
    }
    setSelectedPoDoc(doc);
    setPoModalOpen(true);
  };

  const closePOModal = () => {
    setPoModalOpen(false);
    setSelectedPoDoc(null);
  };

  const openWOModal = (doc) => {
    if (!doc.orderGivenTo?.vendor?._id) {
      alert('No vendor has been awarded this quotation yet.');
      return;
    }
    setSelectedWoDoc(doc);
    setWoModalOpen(true);
  };

  const closeWOModal = () => {
    setWoModalOpen(false);
    setSelectedWoDoc(null);
  };


  const downloadDocs = async filePath => {
    try {
      const response = await fetch(`${API_URL}file/serve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ filePath }),
      });
      if (!response.ok) return;
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filePath.split('/').pop() || 'download';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch { }
  };

  const fetchQuotations = async () => {
    try {
      const response = await fetch(`${API_URL}quotations`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await response.json();
      if (result.error) throw new Error(result.message);
      setData(result.data.docs);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const totalValue = 0; // kept for reference, now computed inside CreatePurchaseOrder

  if (loading) return (
    <div className="flex h-screen items-center justify-center gap-3">
      <Spinner color="primary" />
      <span className="font-semibold text-gray-600">Fetching Procurement Records...</span>
    </div>
  );

  if (error) return (
    <Container className="mt-10">
      <Alert color="danger">Failed to load data: {error}</Alert>
    </Container>
  );

  return (
    <Container fluid className="bg-gray-50 min-h-screen py-8 px-4 lg:px-12">
      <div className="mb-8 border-b pb-4 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-800">Quotation Summary</h1>
          <p className="text-slate-500">Manage vendor submissions and order awards</p>
        </div>
        <Badge color="info" pill className="px-4 py-2 text-sm uppercase tracking-wide">
          {data.length} Records Found
        </Badge>
      </div>

      <Row className="gap-y-6">
        {data.map((doc) => (
          <Col xs="12" key={doc._id} className="mb-6">
            <Card className="shadow-sm border-0 rounded-xl overflow-hidden">
              <CardBody className="p-0">
                <div className="bg-white p-4 border-b flex flex-wrap justify-between items-center gap-4">
                  <div className="flex items-center gap-3 text-slate-700">
                    <div className="bg-blue-100 p-2 rounded-lg text-blue-600">
                      <FileText size={20} />
                    </div>
                    <span className="text-xs uppercase font-bold text-slate-400 tracking-tight">Quotation</span>
                    {(() => {
                      const isOem = doc.isOem ?? doc.purchaseRequests?.some((pr) => pr.isOem)
                      return (
                        <Badge
                          className={
                            isOem
                              ? 'bg-purple-50 text-purple-700 border border-purple-200 font-bold'
                              : 'bg-gray-50 text-gray-600 border border-gray-200 font-bold'
                          }
                        >
                          {isOem ? 'OEM' : 'Non-OEM'}
                        </Badge>
                      )
                    })()}
                  </div>
                  <div className="flex gap-6">
                    <div className="text-right">
                      <span className="text-xs uppercase font-bold text-slate-400 block">Created By</span>
                      <div className="flex items-center gap-1 text-sm font-medium">
                        <User size={14} /> {doc.createdBy?.name}
                      </div>
                    </div>
                    <div className="text-right border-l pl-6">
                      <span className="text-xs uppercase font-bold text-slate-400 block">Date</span>
                      <div className="flex items-center gap-1 text-sm font-medium">
                        <Calendar size={14} /> {new Date(doc.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  <Row>
                    <Col lg="4" className="border-r border-dashed pr-6">
                      <div className="mb-4">
                        <h6 className="flex items-center gap-2 text-green-700 font-bold uppercase text-xs mb-3 tracking-widest">
                          <CheckCircle size={16} /> Approved Quotation
                        </h6>
                        {doc.orderGivenTo ? (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-4 hover:shadow-md transition-all">
                            <h4 className="font-bold text-green-900 m-0">{doc.orderGivenTo.vendor?.vendorName}</h4>
                            <p className="text-sm text-green-700 mb-3">{doc.orderGivenTo.vendor?.vendorEmail}</p>
                            <Button size="sm" color="success" className="w-full flex items-center justify-center gap-2" onClick={() => downloadDocs(doc?.orderGivenTo?.pdfPath)}>
                              <Download size={14} /> View Award Document
                            </Button>
                          </div>
                        ) : (
                          <div className="bg-slate-100 border border-slate-200 rounded-lg p-4 text-center text-slate-400 italic text-sm">Pending Award Selection</div>
                        )}
                      </div>
                      <div className="mt-6">
                        <h6 className="text-xs font-bold uppercase text-slate-400 mb-3 tracking-widest flex items-center gap-2">
                          <Package size={14} /> Linked Purchase Requests
                        </h6>
                        <div className="flex flex-wrap gap-2">
                          {doc.purchaseRequests?.map(pr => (
                            <Badge key={pr._id} className="text-blue-600 border border-blue-100 p-2 flex items-center gap-1.5">
                              {pr.prId}
                              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${pr.isOem ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-500'}`}>
                                {pr.isOem ? 'OEM' : 'Non-OEM'}
                              </span>
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </Col>

                    <Col lg="8" className="pl-6">
                      <h6 className="text-xs font-bold uppercase text-slate-400 mb-4 tracking-widest flex items-center gap-2">
                        <Truck size={16} /> Other Proposed Quotations 
                      </h6>
                      <div className="rounded-lg border border-slate-200 overflow-hidden shadow-sm bg-white">
                        <Table responsive hover borderless className="mb-0 text-sm align-middle">
                          <thead className="bg-slate-50 border-b border-slate-200">
                            <tr>
                              <th className="px-4 py-3 text-slate-500 font-semibold">Vendor</th>
                              <th className="px-4 py-3 text-slate-500 font-semibold">Quotation No</th>
                              <th className="px-4 py-3 text-slate-500 font-semibold">Details</th>
                              <th className="px-4 py-3 text-slate-500 font-semibold text-center">Doc</th>
                            </tr>
                          </thead>
                          <tbody>
                            {doc.quotations.map((q) => (
                              <tr key={q._id} className="border-b last:border-0 hover:bg-slate-50 transition-colors">
                                <td className="px-4 py-3 font-semibold text-slate-700">
                                  {q.vendor?.vendorName}
                                  <span className="block text-xs font-normal text-slate-400 lowercase">{q.vendor?.vendorEmail}</span>
                                </td>
                                <td className="px-4 py-3 text-slate-600 italic">{q.quotationNumber || '-'}</td>
                                <td className="px-4 py-3 text-slate-600 truncate max-w-xs italic">{q.quotationDetails || '-'}</td>
                                <td className="px-4 py-3 text-center">
                                  {q.pdfPath ? (
                                    <Button color="link" className="text-blue-600 p-0" onClick={() => downloadDocs(q?.pdfPath)}><ExternalLink size={18} /></Button>
                                  ) : (<span className="text-slate-300 text-xs">N/A</span>)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </Col>
                  </Row>
                </div>
              </CardBody>
              <CardFooter className="d-flex justify-content-end gap-2">
                <button className="btn btn-primary" onClick={() => openWOModal(doc)} disabled={!doc.orderGivenTo}>Create WO</button>
                <button className="btn btn-success" onClick={() => openPOModal(doc)} disabled={!doc.orderGivenTo}>Create PO</button>
              </CardFooter>
            </Card>
          </Col>
        ))}
      </Row>

      <CreatePurchaseOrder
        isOpen={poModalOpen}
        onClose={closePOModal}
        selectedDoc={selectedPoDoc}
        onSuccess={fetchQuotations}
      />

      <CreateWorkOrder
        isOpen={woModalOpen}
        onClose={closeWOModal}
        selectedDoc={selectedWoDoc}
      />
    </Container>
  );
};

export default ViewQuotation;